import {
  INSIGHTS_COACH_GUIDE_LABEL,
  PEER_INSIGHTS_COACH_GUIDE_URL,
  PULSE_CHECK_COACH_GUIDE_URL,
} from '@/constants';
import { Button, Divider, Icon, Pagination, Paper, Typography } from '@/ui';

import PeerInsightsRow from './PeerInsightsRow';
import PulseCheckRow from './PulseCheckRow';

import type { MemberInsightsPageProps } from './types';

const MemberInsightsPage = ({
  centered = false,
  copy,
  pulseCheck,
  peerInsights,
  viewerRole,
  onPeerViewClick,
  onPulsePageChange,
  onPulseViewClick,
}: MemberInsightsPageProps) => {
  const hasPulseSummaries = pulseCheck.entries.some((entry) => entry.aiSummary);
  const handlePeerHeaderActionClick = () => {
    peerInsights.headerAction?.onClick();
  };
  const handlePeerPrimaryActionClick = () => {
    peerInsights.emptyState?.primaryAction?.onClick();
  };
  const handlePeerSecondaryActionClick = () => {
    peerInsights.emptyState?.secondaryAction?.onClick();
  };
  const handlePulsePrimaryActionClick = () => {
    pulseCheck.emptyState?.primaryAction?.onClick();
  };
  const handlePulseHeaderActionClick = () => {
    pulseCheck.headerAction?.onClick();
  };
  const showCoachGuide = viewerRole !== 'member';
  const showPeerInsightsPanel = !peerInsights.hidden;
  let pulseDescription = copy.pulseCheckDescription;
  let peerDescription = copy.peerInsightsDescription;

  if (pulseCheck.entries.length === 0 && pulseCheck.emptyState?.description) {
    pulseDescription = pulseCheck.emptyState.description;
  }

  if (
    peerInsights.entries.length === 0 &&
    peerInsights.emptyState?.description
  ) {
    peerDescription = peerInsights.emptyState.description;
  }

  const renderPulseContent = () => {
    if (pulseCheck.entries.length === 0) {
      if (pulseCheck.emptyState) {
        return (
          <div className="flex flex-col items-start gap-4">
            {pulseCheck.emptyState.message && (
              <Typography className="text-cement-400" variant="body2">
                {pulseCheck.emptyState.message}
              </Typography>
            )}
            {pulseCheck.emptyState.primaryAction && (
              <Button
                className="px-8"
                color="secondary"
                variant="contained"
                onClick={handlePulsePrimaryActionClick}
              >
                {pulseCheck.emptyState.primaryAction.label}
              </Button>
            )}
          </div>
        );
      }

      return (
        <>
          <Divider className="mb-4" />
          <Typography className="text-cement-400" variant="body2">
            {copy.pulseCheckEmptyState}
          </Typography>
        </>
      );
    }

    return (
      <div>
        <Divider className="mb-4" />
        {pulseCheck.entries.map((entry, index) => (
          <div key={entry.id}>
            <PulseCheckRow
              entry={entry}
              viewLabel={copy.viewLabel}
              onViewClick={onPulseViewClick}
            />
            {index < pulseCheck.entries.length - 1 && <Divider />}
          </div>
        ))}

        {hasPulseSummaries && (
          <>
            <Divider className="mt-1" />
            <Typography className="pt-3 text-cement-400" variant="caption">
              {copy.aiSummariesLabel}
            </Typography>
          </>
        )}

        {pulseCheck.pagination && pulseCheck.pagination.totalPages > 1 && (
          <Pagination
            className="pt-2"
            count={pulseCheck.pagination.totalPages}
            page={pulseCheck.pagination.currentPage}
            size="small"
            showFirstButton
            showLastButton
            onChange={(_, page) => onPulsePageChange?.(page)}
          />
        )}
      </div>
    );
  };

  const renderPeerMedia = () => {
    if (!peerInsights.emptyState?.media) {
      return null;
    }

    if (peerInsights.emptyState.media.type === 'video') {
      return (
        <video
          className="size-full object-cover"
          src={peerInsights.emptyState.media.src}
          controls
        >
          {peerInsights.emptyState.media.alt}
        </video>
      );
    }

    return (
      <img
        alt={peerInsights.emptyState.media.alt}
        className="m-auto h-auto w-full"
        src={peerInsights.emptyState.media.src}
      />
    );
  };

  const renderPeerContent = () => {
    if (peerInsights.entries.length > 0) {
      return (
        <>
          <Divider className="mb-1" />
          <div>
            {peerInsights.entries.map((entry, index) => (
              <div key={entry.id}>
                <PeerInsightsRow
                  entry={entry}
                  viewLabel={copy.viewLabel}
                  onViewClick={onPeerViewClick}
                />
                {index < peerInsights.entries.length - 1 && <Divider />}
              </div>
            ))}
          </div>
        </>
      );
    }

    if (!peerInsights.emptyState) {
      return null;
    }

    return (
      <div className="flex flex-col items-start gap-4">
        <div className="flex flex-wrap gap-2">
          {peerInsights.emptyState.primaryAction && (
            <Button
              className="px-8"
              color="secondary"
              variant="contained"
              onClick={handlePeerPrimaryActionClick}
            >
              {peerInsights.emptyState.primaryAction.label}
            </Button>
          )}
          {peerInsights.emptyState.secondaryAction && (
            <Button
              className="border-black px-8 text-black"
              variant="outlined"
              onClick={handlePeerSecondaryActionClick}
            >
              {peerInsights.emptyState.secondaryAction.label}
            </Button>
          )}
        </div>
        {renderPeerMedia()}
      </div>
    );
  };

  const renderCoachGuideLink = (href: string) => (
    <Button
      color="primary"
      endIcon={<Icon fontSize="small">open_in_new</Icon>}
      href={href}
      rel="noopener noreferrer"
      size="small"
      target="_blank"
      variant="outlined"
    >
      {INSIGHTS_COACH_GUIDE_LABEL}
    </Button>
  );

  return (
    <div
      className={
        centered
          ? 'mx-auto w-full max-w-[1020px] px-4 pb-4 pt-12'
          : 'container mx-auto max-w-screen-lg px-4 pb-4 pt-12'
      }
    >
      <Typography className="mb-6" variant="h4">
        {copy.heading}
      </Typography>

      <div className="grid w-full max-w-[1020px] grid-cols-1 items-start gap-6 lg:grid-cols-2">
        <Paper className="shadow-elevation-1 rounded-xl border border-cement-300 p-6">
          <div className="mb-4 flex items-start justify-between gap-4">
            <div className="flex size-12 items-center justify-center rounded-full bg-bloom-500">
              <Icon className="text-white">favorite</Icon>
            </div>

            {pulseCheck.headerAction ? (
              <Button
                color="primary"
                size="small"
                variant="contained"
                light
                onClick={handlePulseHeaderActionClick}
              >
                {pulseCheck.headerAction.label}
              </Button>
            ) : (
              showCoachGuide &&
              renderCoachGuideLink(PULSE_CHECK_COACH_GUIDE_URL)
            )}
          </div>

          <Typography className="mb-2 text-cement-500" variant="h5">
            Pulse Check
          </Typography>

          <Typography className="mb-4 text-cement-500" variant="body1">
            {pulseDescription}
          </Typography>

          {renderPulseContent()}
        </Paper>

        {showPeerInsightsPanel && (
          <Paper className="shadow-elevation-1 rounded-xl border border-cement-300 p-6">
            <div className="mb-4 flex items-start justify-between gap-4">
              <div className="flex size-12 items-center justify-center rounded-full bg-bloom-500">
                <Icon className="text-white">assignment</Icon>
              </div>

              {peerInsights.headerAction ? (
                <Button
                  color="primary"
                  size="small"
                  variant="contained"
                  light
                  onClick={handlePeerHeaderActionClick}
                >
                  {peerInsights.headerAction.label}
                </Button>
              ) : (
                showCoachGuide &&
                renderCoachGuideLink(PEER_INSIGHTS_COACH_GUIDE_URL)
              )}
            </div>

            <Typography className="mb-2 text-cement-500" variant="h5">
              Peer Insights
            </Typography>

            <Typography className="mb-4 text-cement-500" variant="body1">
              {peerDescription}
            </Typography>

            {renderPeerContent()}
          </Paper>
        )}
      </div>
    </div>
  );
};

export default MemberInsightsPage;
